import {
  CalendarEvent,
  CalendarEventWithRelations,
  CalendarEventWithTags,
  CalendarEventComment,
  CalendarTag,
  CreateCalendarEventRequest,
  UpdateCalendarEventRequest,
  CreateCalendarTagRequest,
  UpdateCalendarTagRequest,
} from '../types/calendar';

const CALENDAR_API_BASE_URL = 'https://spy.playcdu.co/api/notes/calendar';

class CalendarApiService {
  private async request<T>(endpoint: string, options?: RequestInit): Promise<T> {
    const response = await fetch(`${CALENDAR_API_BASE_URL}${endpoint}`, {
      ...options,
      // Cloudflare Access handles authentication via cookies
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`API Error: ${response.status} ${response.statusText} - ${errorText}`);
    }

    return response.json();
  }

  // Calendar Tags
  async createTag(data: CreateCalendarTagRequest): Promise<CalendarTag> {
    return this.request<CalendarTag>('/tags', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
  }

  async getAllTags(): Promise<CalendarTag[]> {
    return this.request<CalendarTag[]>('/tags');
  }

  async getTagById(id: string): Promise<CalendarTag> {
    return this.request<CalendarTag>(`/tags/${id}`);
  }

  async updateTag(id: string, data: UpdateCalendarTagRequest): Promise<CalendarTag> {
    return this.request<CalendarTag>(`/tags/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
  }

  async deleteTag(id: string): Promise<{ ok: boolean; deleted: string }> {
    return this.request<{ ok: boolean; deleted: string }>(`/tags/${id}`, {
      method: 'DELETE',
    });
  }

  // Calendar Events
  async createEvent(data: CreateCalendarEventRequest): Promise<CalendarEvent> {
    return this.request<CalendarEvent>('/events', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
  }

  async getAllEvents(params?: {
    start_time?: number;
    end_time?: number;
    status?: string;
    /** Relations to embed in each event, e.g. ['tags']. Saves a request per event. */
    include?: ('tags' | 'users')[];
    limit?: number;
  }): Promise<CalendarEvent[]> {
    const queryParams = new URLSearchParams();
    if (params?.start_time) queryParams.append('start_time', params.start_time.toString());
    if (params?.end_time) queryParams.append('end_time', params.end_time.toString());
    if (params?.status) queryParams.append('status', params.status);
    if (params?.include?.length) queryParams.append('include', params.include.join(','));
    if (params?.limit) queryParams.append('limit', params.limit.toString());

    const queryString = queryParams.toString();
    return this.request<CalendarEvent[]>(`/events${queryString ? `?${queryString}` : ''}`);
  }

  /**
   * Events with their tags embedded by the backend. Use this instead of calling
   * getEventById once per event, which costs one HTTP round trip per event.
   */
  async getEventsWithTags(params?: {
    start_time?: number;
    end_time?: number;
    status?: string;
  }): Promise<CalendarEventWithTags[]> {
    return this.getAllEvents({ ...params, include: ['tags'] }) as Promise<CalendarEventWithTags[]>;
  }

  async getEventById(id: string): Promise<CalendarEventWithRelations> {
    return this.request<CalendarEventWithRelations>(`/events/${id}`);
  }

  async updateEvent(id: string, data: UpdateCalendarEventRequest): Promise<CalendarEvent> {
    return this.request<CalendarEvent>(`/events/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
  }

  async deleteEvent(id: string): Promise<{ ok: boolean; deleted: string }> {
    return this.request<{ ok: boolean; deleted: string }>(`/events/${id}`, {
      method: 'DELETE',
    });
  }

  async cancelEvent(id: string): Promise<CalendarEvent> {
    return this.request<CalendarEvent>(`/events/${id}/cancel`, {
      method: 'POST',
    });
  }

  async postponeEvent(id: string): Promise<CalendarEvent> {
    return this.request<CalendarEvent>(`/events/${id}/postpone`, {
      method: 'POST',
    });
  }

  // Event Comments
  async addComment(eventId: string, content: string): Promise<CalendarEventComment> {
    return this.request<CalendarEventComment>(`/events/${eventId}/comments`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ content }),
    });
  }

  async getComments(eventId: string): Promise<CalendarEventComment[]> {
    return this.request<CalendarEventComment[]>(`/events/${eventId}/comments`);
  }

  // Event Tags
  async addTagToEvent(eventId: string, tagId: string): Promise<{ ok: boolean }> {
    return this.request<{ ok: boolean }>(`/events/${eventId}/tags/${tagId}`, {
      method: 'POST',
    });
  }

  async removeTagFromEvent(eventId: string, tagId: string): Promise<{ ok: boolean }> {
    return this.request<{ ok: boolean }>(`/events/${eventId}/tags/${tagId}`, {
      method: 'DELETE',
    });
  }

  // Event Users
  async addUserToEvent(eventId: string, userUuid: string): Promise<{ ok: boolean }> {
    return this.request<{ ok: boolean }>(`/events/${eventId}/users/${userUuid}`, {
      method: 'POST',
    });
  }

  async removeUserFromEvent(eventId: string, userUuid: string): Promise<{ ok: boolean }> {
    return this.request<{ ok: boolean }>(`/events/${eventId}/users/${userUuid}`, {
      method: 'DELETE',
    });
  }

  // Event Queries
  async getEventsByUser(userUuid: string): Promise<CalendarEvent[]> {
    return this.request<CalendarEvent[]>(`/events/user/${userUuid}`);
  }

  async getEventsByTag(tagId: string): Promise<CalendarEvent[]> {
    return this.request<CalendarEvent[]>(`/events/tag/${tagId}`);
  }

  // ICS Publish to KV Store
  async generateICS(params?: {
    start_time?: number;
    end_time?: number;
    status?: string;
  }): Promise<{ ok: boolean }> {
    const queryParams = new URLSearchParams();
    if (params?.start_time) queryParams.append('start_time', params.start_time.toString());
    if (params?.end_time) queryParams.append('end_time', params.end_time.toString());
    if (params?.status) queryParams.append('status', params.status);

    const queryString = queryParams.toString();
    return this.request<{ ok: boolean }>(`/generate-ics${queryString ? `?${queryString}` : ''}`, {
      method: 'POST',
    });
  }
}

export const calendarApiService = new CalendarApiService();

